/**
 * Oendies free gift: waszakje bij eerste bestelling.
 *
 * Adds the gift variant when incontinentie-ondergoed items in the cart reach
 * the threshold (after discounts) and removes it as soon as the cart drops
 * below. Logged-in customers with previous orders never get the gift.
 * Config comes from snippets/oendies-free-gift.liquid.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SOURCE = 'oendies-free-gift';
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const config = window.OendiesFreeGift;
// Own requests use the unpatched fetch so they don't retrigger the sync.
const nativeFetch = window.fetch.bind(window);

let collectionIds = null;
let running = false;
let rerun = false;

async function getCollectionIds() {
  if (collectionIds) return collectionIds;
  const cacheKey = `${SOURCE}:${config.collection}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (Array.isArray(cached)) return (collectionIds = new Set(cached));
  } catch (e) {}

  const ids = [];
  for (let page = 1; page <= 10; page++) {
    const res = await nativeFetch(`/collections/${config.collection}/products.json?limit=250&page=${page}`);
    if (!res.ok) throw new Error(`Collection fetch failed: ${res.status}`);
    const { products } = await res.json();
    ids.push(...products.map((p) => p.id));
    if (products.length < 250) break;
  }
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(ids));
  } catch (e) {}
  return (collectionIds = new Set(ids));
}

// Threshold is set in EUR; cart.js returns amounts in the active currency.
function thresholdInCents() {
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  return Math.round(config.thresholdCents * rate);
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function updateCart(updates) {
  const sections = cartSectionIds();
  const res = await nativeFetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
  });
  if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
  const cart = await res.json();

  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections: cart.sections || {},
    })
  );
  // The Oendies cart drawer (assets/cart-drawer.js) re-renders on this event.
  document.dispatchEvent(new CustomEvent('oendies:cart-refresh'));
}

async function sync() {
  const cart = await nativeFetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' }).then((r) => r.json());
  const giftLines = cart.items.filter((item) => item.variant_id === config.giftVariantId);
  const otherLines = cart.items.filter((item) => item.variant_id !== config.giftVariantId);

  let eligible = false;
  if (!config.returningCustomer && otherLines.length) {
    const ids = await getCollectionIds();
    const qualifying = otherLines
      .filter((item) => ids.has(item.product_id))
      .reduce((sum, item) => sum + item.final_line_price, 0);
    eligible = qualifying >= thresholdInCents();
  }

  const updates = {};
  if (eligible) {
    if (!giftLines.length) updates[config.giftVariantId] = 1;
    giftLines.forEach((line, index) => {
      const wanted = index === 0 ? 1 : 0;
      if (line.quantity !== wanted) updates[line.key] = wanted;
    });
  } else {
    giftLines.forEach((line) => (updates[line.key] = 0));
  }

  if (Object.keys(updates).length) await updateCart(updates);
}

async function run() {
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    do {
      rerun = false;
      await sync();
    } while (rerun);
  } catch (error) {
    console.error('[oendies-free-gift]', error);
  } finally {
    running = false;
  }
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  return input instanceof Request ? input.url : String(input);
}

if (config) {
  // The custom cart drawer changes the cart without theme events, so watch
  // cart requests directly.
  window.fetch = function (input, init) {
    const request = nativeFetch(input, init);
    if (CART_MUTATION.test(requestUrl(input).split('#')[0])) {
      request.then((res) => res.ok && run()).catch(() => {});
    }
    return request;
  };
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    run();
  });
  run();
}
